<template>
  <div>
    <v-data-table
      :value="selected"
      :loading="loading"
      :headers="headers"
      :items="payrolls"
      :items-per-page="100"
      :server-items-length="meta.total"
      :footer-props="footerProps"
      :options.sync="localOptions"
      :sort-by="sortBy"
      sort-desc
      show-select
      @input="$emit('changeSelected', $event)"
    >
      <template v-slot:item.expenses="{ item }">
        {{ item.expenses | priceFormat }}
      </template>

      <template v-slot:item.action="{ item }">
        <action-slot-component
          :item="item"
          route-name="ereporter.payroll.show"
          open-in-new-window
          :route-params="{ id: item.id }"
          @show="$emit('show', item)"
        />
      </template>

      <template v-slot:item.employee="{ item }">
        <span class="mr-2">{{ item.employee }}</span>
        <router-link
          class="text-decoration-none"
          target="_blank"
          :to="{
            name: 'employees.show',
            params: { id: item.employee_id }
          }"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">
                mdi-eye-outline
              </v-icon>
            </template>
            <span v-if="item.employee">{{ item.employee }}</span>
            <span v-else>{{ $t('ITEM.VIEW') }}</span>
          </v-tooltip>
        </router-link>
      </template>
    </v-data-table>

    <v-row>
      <v-col cols="2">
        {{ $t('EREPORTER.SELECTED') }}: {{ selected.length }}
      </v-col>
      <v-col cols="3">
        <v-switch
          v-model="showSelected"
          dense
          class="mt-0 pt-0"
          hide-details
          :label="$t('EREPORTER.SHOW_SELECTED')"
          @change="$emit('changeShowSelect', $event)"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="3">
        <v-select
          v-model="selectedStatus"
          item-value="id"
          item-text="name"
          return-object
          :items="payrollMassStatuses"
          :label="$t('EREPORTER.SET_STATUS')"
          :disabled="!selected.length"
          @change="setStatus"
        ></v-select>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-btn :disabled="disableExport()">
          <JsonExcel
            :fetch="getPayrollsExport"
            :fields="exportPayrollFields"
            :name="`${$t('REPORTS.PAYROLLS') + datetime}.xls`"
          >
            {{ $t('BUTTON.EXPORT_PAYROLLS') }}
          </JsonExcel>
        </v-btn>
      </v-col>
    </v-row>

    <confirm-dialog ref="confirm"></confirm-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { mapCacheActions } from 'vuex-cache';
import { priceFormat } from '@/modules/_global/filters';
import { types } from '@/modules/ereporter/common/types.js';
import JsonExcel from 'vue-json-excel';
import downloadFile from '@/modules/_global/mixins/download-file';
import ActionSlotComponent from '@/modules/_global/_components/ActionSlotComponent.vue';

export default {
  name: 'PayrollsList',

  filters: { priceFormat },

  components: {
    ActionSlotComponent,
    ConfirmDialog: () => import('@/modules/_global/_components/ConfirmDialog'),
    JsonExcel
  },

  mixins: [downloadFile],

  model: {
    prop: 'selected',
    event: 'changeSelected'
  },

  props: {
    payrolls: {
      type: Array,
      default: () => []
    },
    selected: {
      type: Array,
      default: () => []
    },
    meta: {
      type: Object,
      default: () => ({
        total: 0
      })
    },
    options: {
      type: Object,
      default: () => {}
    },
    exportOptions: {
      type: Object,
      default: () => {}
    }
  },

  data: () => ({
    unwatch: null,
    localOptions: {},
    selectedStatus: null,
    sortBy: 'id',
    footerProps: {
      showFirstLastPage: true,
      itemsPerPageOptions: [10, 50, 100]
    },
    types: types,
    datetime: '',
    showSelected: false
  }),

  computed: {
    ...mapState(['loading']),
    ...mapState('ereporter', ['payrollMassStatuses']),
    params() {
      const options = this.localOptions;

      return {
        page: options.page,
        per_page: options.itemsPerPage,
        sort_by: options.sortBy[options.sortBy.length - 1],
        order_by: options.sortDesc[options.sortDesc.length - 1] ? 'desc' : 'asc'
      };
    },
    headers() {
      return [
        {
          text: this.$t('ITEM.ID'),
          sortable: true,
          value: 'id'
        },
        {
          text: this.$t('EREPORTER.PERIOD'),
          sortable: true,
          value: 'period'
        },
        {
          text: this.$t('EMPLOYEES.ITEM'),
          sortable: true,
          value: 'employee'
        },
        {
          text: this.$t('EREPORTER.PAYROLLS.EMPLOYEE_TYPE'),
          sortable: true,
          value: 'employee_type'
        },
        {
          text: this.$t('EREPORTER.DURATION'),
          sortable: true,
          value: 'duration'
        },
        {
          text: this.$t('ITEM.STATUS'),
          sortable: true,
          value: 'status'
        },
        {
          text: this.$t('DATA_TABLE.ACTIONS'),
          value: 'action',
          sortable: false,
          align: 'center'
        }
      ];
    },
    exportPayrollFields() {
      return {
        [this.$t('ITEM.ID')]: 'id',
        [this.$t('EREPORTER.PERIOD')]: 'period',
        [this.$t('EMPLOYEES.ITEM')]: 'employee',
        [this.$t('EMPLOYEES.ABACUS_ID')]: 'employee_abacus_id',
        [this.$t('EREPORTER.PAYROLLS.EMPLOYEE_TYPE')]: 'employee_type',
        [this.$t('EREPORTER.DURATION')]: 'duration_hours_float',
        [this.$t('ITEM.STATUS')]: 'status'
      };
    }
  },

  watch: {
    localOptions: {
      handler() {
        this.refreshData();
      },
      deep: true
    },
    options: {
      handler() {
        this.localOptions.page = 1;
        this.refreshData();
      },
      deep: true
    }
  },

  created() {
    this.datetime = new Date().toISOString().slice(0, 10);
    this.unwatch = this.$store.watch(
      state => state.localization.lang,
      () => {
        const lang = this.$store.state.localization.lang;

        if (lang) {
          this.refreshData();
        }
      }
    );
  },

  destroyed() {
    this.unwatch();
  },

  methods: {
    ...mapActions('ereporter', ['updatePayrollsStatuses']),
    ...mapCacheActions('ereporter', ['getPayrollMassStatuses']),
    setStatus(status) {
      this.$refs.confirm
        .open(
          this.$t('EREPORTER.PAYROLLS.MASS_CHANGE_STATUS_QUESTION', {
            count: this.selected.length
          }),
          null,
          {
            cancelBtnText: this.$t(
              'EREPORTER.MASS_CHANGE_STATUS_QUESTION_CANCEL_BUTTON'
            ),
            agreeBtnText: this.$t(
              'EREPORTER.MASS_CHANGE_STATUS_QUESTION_AGREE_BUTTON'
            )
          }
        )
        .then(confirm => {
          if (confirm) {
            this.updatePayrollsStatuses({
              status,
              ids: this.selected.map(i => i.id)
            });
          }
        })
        .finally(() => {
          this.selectedStatus = null;
        });
    },
    refreshData() {
      this.getPayrollMassStatuses();
      this.$emit('refresh', {
        params: Object.assign({}, this.options, this.params)
      });
    },
    cleanExportOptions() {
      delete this.exportOptions.page;
      delete this.exportOptions.per_page;
      delete this.exportOptions.sort_by;
      delete this.exportOptions.order_by;

      delete this.exportOptions.timeChangeExportOptions;
      delete this.exportOptions.timeClickFindButton;
    },
    getPayrollsExport() {
      this.cleanExportOptions();

      return this.$store
        .dispatch('report/retrievePayrolls', { params: this.exportOptions })
        .then(response => {
          return response.data.data.map(i => {
            return {
              ...i,
              // special for Microsoft Office Excel
              duration_hours_float: i.duration_hours_float
                .toFixed(2)
                .replace('.', ',')
            };
          });
        })
        .catch(error => {
          this.error = error.response;
        });
    },
    disableExport() {
      let options = this.exportOptions;
      if (!options) {
        return true;
      }

      if (options.timeClickFindButton < options.timeChangeExportOptions) {
        return true;
      }

      let isEmptyFilter = true;

      if (
        options.period ||
        options.employee ||
        (options.status && options.status.length)
      ) {
        isEmptyFilter = false;
      }

      return isEmptyFilter;
    }
  }
};
</script>
